import dayjs from 'dayjs';
import React from 'react';
import { Box, Heading, Spinner } from '@apia/theme';
import { useAppSelector } from '../../../../hooks/hooks';
import store from '../../../../store';
import { responsiveTableActions } from '../../../../store/responsiveTableSlice';
import { TDateFormat } from '../../../../types';
import { getDateFormat } from '../../../../utils/date';
import {
  autoDisconnectMutationObserver,
  getSpecificParent,
} from '../../../../utils/dom';
import { arrayOrArray, noNaN } from '../../../../utils/util';

import ResponsiveTable from '../../../common/generic/responsiveTable';
import ResponsiveTableContext from '../../../common/generic/responsiveTable/ResponsiveTableContext';
import {
  TResponsiveTableCell,
  TResponsiveTableColumn,
  TResponsiveTableRow,
} from '../../../common/generic/responsiveTable/types';
import SchedulerTableCell from './SchedulerTableCell';

const dateFormat: TDateFormat = getDateFormat();

const SchedulerTable = () => {
  const data = useAppSelector((global) => global.schedulerSlice.data);
  const [ref, setRef] = React.useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    if (ref)
      autoDisconnectMutationObserver(ref, () => {
        const firstNonDisabledButton = ref.querySelector(
          '.sched_cell:not(:disabled)',
        ) as HTMLElement;
        if (firstNonDisabledButton) {
          const cell = getSpecificParent(
            firstNonDisabledButton,
            (current) => current.tagName === 'TD',
          );
          if (cell) {
            const rowIndex = noNaN(cell.getAttribute('data-rowindex'), -1);
            const ariaColIndex = noNaN(cell.getAttribute('data-colindex'), -1);

            if (rowIndex !== -1 && ariaColIndex !== -1) {
              store.dispatch(
                responsiveTableActions.updateByClick({
                  id: 'SchedulerTable',
                  ariaColIndex,
                  rowIndex,
                  isFocused: true,
                }),
              );
            }
          }
        }
      });
  }, [ref, data]);
  const columns: TResponsiveTableColumn[] = React.useMemo(
    () => [
      {
        label: window.TSK_SCH_HOUR,
        name: 'Hour',
        allowSorting: false,
        width: '75px',
      },
      {
        label: `${window.TSK_DAY_MON}  ${dayjs(
          data?.mondayWeek,
          dateFormat,
        ).format('DD/MM')}`,
        name: 'Monday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_TUE}  ${dayjs(data?.mondayWeek, dateFormat)
          .add(1, 'day')
          .format('DD/MM')}`,
        name: 'Tuesday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_WED} ${dayjs(data?.mondayWeek, dateFormat)
          .add(2, 'day')
          .format('DD/MM')} `,
        name: 'Wednesday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_THU} ${dayjs(data?.mondayWeek, dateFormat)
          .add(3, 'day')
          .format('DD/MM')}`,
        name: 'Thursday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_FRI} ${dayjs(data?.mondayWeek, dateFormat)
          .add(4, 'day')
          .format('DD/MM')}`,
        name: 'Friday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_SAT} ${dayjs(data?.mondayWeek, dateFormat)
          .add(5, 'day')
          .format('DD/MM')}`,
        name: 'Saturday',
        allowSorting: false,
      },
      {
        label: `${window.TSK_DAY_SUN} ${dayjs(data?.mondayWeek, dateFormat)
          .add(6, 'day')
          .format('DD/MM')}`,
        name: 'Sunday',
        allowSorting: false,
      },
    ],
    [data?.mondayWeek],
  );
  const [rows, setRows] = React.useState<TResponsiveTableRow[] | null>(null);
  const overassign = useAppSelector((global) => {
    return global.schedulerSlice?.data?.overassign;
  });

  React.useEffect(() => {
    const retrievedDays = arrayOrArray(data?.tskSchDay);
    const hourIndexes = arrayOrArray(
      retrievedDays.find((c) => c?.tskSchHour)?.tskSchHour,
    );

    const newRows = hourIndexes
      .map((hour, rowIdx) => {
        const currentDate = dayjs();
        const currentHour = Number(dayjs().format('HHmm'));
        const newRow: TResponsiveTableRow = {
          id: hour.hour,
          cells: [
            {
              children: `${hour.hour.slice(0, -2)}:${hour.hour.slice(2)}`,
            },
            ...columns.map((_, columnIdx) => {
              const currentDayDefinition = retrievedDays[columnIdx];
              const currentHourDefinition = arrayOrArray(
                currentDayDefinition?.tskSchHour,
              )[rowIdx];

              const parsingDay = dayjs(currentDayDefinition?.day, dateFormat);
              const parsingHour = Number(currentHourDefinition?.hour);

              const isEnabled =
                !hour?.disabled &&
                Number(currentHourDefinition?.value) + Number(overassign) > 0 &&
                (!(
                  parsingDay.isSame(currentDate, 'day') ||
                  parsingDay.isBefore(currentDate, 'day')
                ) ||
                  (parsingDay.isSame(currentDate, 'day') &&
                    currentHour <= parsingHour));

              const isQueriedDisabled = hour?.disabled;

              const returnCell: TResponsiveTableCell = {
                Renderer: SchedulerTableCell,
                rendererProps: {
                  cellId: `${hour.hour} ${columnIdx + 1} ${
                    data?.mondayWeek ?? ''
                  }`,
                  currentDayDefinition,
                  isEnabled,
                  isQueriedDisabled,
                  weekDay: data?.mondayWeek ?? '',
                  dayNumber: columnIdx + 1,
                  hour: hour.hour,
                  value: Number(currentHourDefinition?.value),
                  valuePlusOverassign:
                    Number(currentHourDefinition?.value) + Number(overassign),
                  isOverassignClassName: !!(
                    !Number(currentHourDefinition?.value) &&
                    Number(currentHourDefinition?.value) < Number(overassign)
                  ),
                },
              };
              return returnCell;
            }),
          ],
        };

        if (
          newRow.cells.find((search) => search.rendererProps?.isQueriedDisabled)
        )
          return null;

        return newRow;
      })
      .filter((current): current is TResponsiveTableRow => current !== null);

    setRows(newRows);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, overassign]);

  return (
    <>
      {data?.weekNotConfigured && (
        <Heading as="h1">{window.LBL_SCHED_WEEK_NOT_CONFIGURED}</Heading>
      )}
      {!data?.weekNotConfigured && data?.tskSchDay && rows && (
        <>
          <ResponsiveTableContext
            columns={columns}
            rows={rows}
            allowSelection={false}
            name="SchedulerTable"
            allowEdition
          >
            <ResponsiveTable className="schedulerTable" ref={setRef} />
          </ResponsiveTableContext>
        </>
      )}
      {!data?.weekNotConfigured && !data?.tskSchDay && (
        <Box className="box_spinner" title={window.WAIT_A_SECOND}>
          <Spinner />
        </Box>
      )}
    </>
  );
};
export default SchedulerTable;
